import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const ProjectCost = () => {
  // State variables for Project Cost calculation
  const [laborCost, setLaborCost] = useState(0); // Cost for labor involved in the project
  const [materialCost, setMaterialCost] = useState(0); // Cost for materials needed for the project
  const [overhead, setOverhead] = useState(0); // Overhead cost for the project
  const [duration, setDuration] = useState(0); // Duration of the project (in months or years)
  const [totalCost, setTotalCost] = useState(0); // Calculated total project cost

  // Event handlers to update state values
  const handleLaborCostChange = (e) =>
    setLaborCost(parseFloat(e.target.value) || 0);
  const handleMaterialCostChange = (e) =>
    setMaterialCost(parseFloat(e.target.value) || 0);
  const handleOverheadChange = (e) =>
    setOverhead(parseFloat(e.target.value) || 0);
  const handleDurationChange = (e) =>
    setDuration(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate project cost when any input changes
  useEffect(() => {
    const projectCost = (laborCost + materialCost + overhead) * duration;
    setTotalCost(projectCost);

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [laborCost, materialCost, overhead, duration]);

  return (
    <Layout title="Project Cost Calculator" path="project-cost">
      <section className="section-padding" id="project-cost-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Project Cost Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Project Cost Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Labor Cost Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="laborCost" className="form-label">
                        Labor Cost (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Labor Cost:</strong> The total cost of labor involved in the project."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="laborCost"
                        placeholder="Enter labor cost"
                        value={laborCost || ""}
                        onChange={handleLaborCostChange}
                      />
                    </div>

                    {/* Material Cost Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="materialCost" className="form-label">
                        Material Cost (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Material Cost:</strong> The cost of materials needed for the project."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="materialCost"
                        placeholder="Enter material cost"
                        value={materialCost || ""}
                        onChange={handleMaterialCostChange}
                      />
                    </div>

                    {/* Overhead Cost Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="overhead" className="form-label">
                        Overhead (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Overhead Cost:</strong> Additional costs like utilities, administrative expenses."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="overhead"
                        placeholder="Enter overhead cost"
                        value={overhead || ""}
                        onChange={handleOverheadChange}
                      />
                    </div>

                    {/* Project Duration Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="duration" className="form-label">
                        Project Duration (Months)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Project Duration:</strong> The total time duration of the project, in months."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="duration"
                        placeholder="Enter project duration"
                        value={duration || ""}
                        onChange={handleDurationChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Total Project Cost:{" "}
                        <span className="fw-bold ms-3">
                          {totalCost === 0 ? "0.00" : totalCost.toFixed(2)} ₹
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Project Cost Calculator
                </h3>

                {/* What is a Project Cost Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is a Project Cost Calculator?
                </h4>
                <p>
                  A **Project Cost Calculator** helps businesses and
                  organizations estimate the total cost of a project by
                  considering various factors such as labor, material costs,
                  overheads, and project duration. It allows users to quickly
                  get an estimate of the overall cost of the project.
                </p>

                {/* Why Use a Project Cost Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use a Project Cost Calculator?
                </h4>
                <p>
                  This calculator helps you determine the total budget required
                  for your project by calculating labor, material, and overhead
                  costs. It ensures that project managers can make informed
                  decisions, stay within budget, and prevent cost overruns.
                </p>

                {/* Benefits of Using a Project Cost Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a Project Cost Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Accurate Project Budgeting:</strong> Helps estimate
                    the total cost of a project, providing a clear view of the
                    financial requirements.
                  </li>
                  <li>
                    <strong>Improved Resource Allocation:</strong> Helps in
                    determining the necessary resources, both human and
                    material, for the project.
                  </li>
                  <li>
                    <strong>Ensures Feasibility and Profitability:</strong>{" "}
                    Assists in determining whether the project is financially
                    viable and sustainable.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use a Project Cost Calculator?
                </h4>
                <p>
                  This calculator is ideal for businesses and individuals
                  involved in managing projects in various industries,
                  including:
                </p>
                <ul>
                  <li>
                    <strong>Construction:</strong> Helps construction managers
                    estimate project costs based on labor, materials, and
                    duration.
                  </li>
                  <li>
                    <strong>Software Development:</strong> Useful for software
                    development teams to estimate the costs of development based
                    on time and resource requirements.
                  </li>
                  <li>
                    <strong>Event Management:</strong> Helps event planners
                    estimate the cost of organizing an event based on labor and
                    material needs.
                  </li>
                  <li>
                    <strong>Consulting:</strong> Assists consultants in
                    estimating the cost of their services for various projects.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  How do I calculate the total project cost?
                </h4>
                <p>
                  The total project cost is calculated by summing the labor
                  cost, material cost, and overhead cost, and then multiplying
                  it by the project duration.
                </p>

                <h4 className="mt-4 mb-3">
                  Why is it important to track project costs?
                </h4>
                <p>
                  Tracking project costs ensures that the project stays within
                  budget, and helps project managers identify potential
                  financial issues early on. By calculating costs upfront,
                  businesses can ensure profitability and avoid unexpected
                  expenses.
                </p>

                <h4 className="mt-4 mb-3">
                  Can this calculator help with project feasibility?
                </h4>
                <p>
                  Yes, by providing an estimate of the total project cost, this
                  calculator can help you determine whether the project is
                  financially feasible and if it aligns with the available
                  budget.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ProjectCost;
